import { getWindow } from '@/helpers/globals'
import type { MeFragment } from '@/graphql/api'
import type { Ref } from 'vue'
import { type App, inject } from 'vue'
import {
    AnalyticsBrowser,
    type EventProperties,
    type ID,
    type Options,
    type SegmentEvent,
    type Traits,
} from '@segment/analytics-next'

type EventType = SegmentEvent['type'] | 'reset'

export type TrackBuffer = {
    type: EventType
    eventName?: string
    properties?: EventProperties
    traits?: Traits
    options?: Options
    userId?: ID
}

export type Tracked = TrackBuffer[]

declare global {
    interface Window {
        __test_tracked: Tracked
    }
}

const createSegment = (
    analytics: AnalyticsBrowser,
    user: Ref<MeFragment | undefined | null>
): Segment => {
    const track = (...args: Parameters<AnalyticsBrowser['track']>) => {
        if (user.value?.customer?.disabledThirdPartyCookies) {
            return
        }

        analytics.track(...args)
    }

    const login = (me: MeFragment) => {
        if (me.customer?.disabledThirdPartyCookies) {
            return
        }

        analytics.identify(me.id, normalizeMeFragmentIdentify(me))
        analytics.track('login', normalizeMeFragmentLoginLogout(me))
    }

    const logout = (me: MeFragment | null | undefined) => {
        if (me) {
            if (me.customer?.disabledThirdPartyCookies) {
                return
            }
            analytics.track('logout', normalizeMeFragmentLoginLogout(me))
        }

        analytics.reset()
    }

    const page = (args: TrackPageArgs): void => {
        if (user.value?.customer?.disabledThirdPartyCookies) {
            return
        }

        analytics.page(args)
    }

    return {
        track,
        login,
        logout,
        page,
    }
}

const createNoopSegment = (): Segment => ({
    track() {},
    login() {},
    logout() {},
    page() {},
})

export const installAnalytics = (
    app: App,
    key: string | undefined,
    user: Ref<MeFragment | undefined | null>
): void => {
    const analytics = createAnalytics(key)

    const segment = analytics
        ? createSegment(analytics, user)
        : createNoopSegment()

    provideSegment(app, segment)
}

const createAnalytics = (
    key: string | undefined
): AnalyticsBrowser | undefined => {
    if (import.meta.env.SSR || !key) {
        return
    }

    const analyticsBrowser = new AnalyticsBrowser()

    analyticsBrowser.load({
        writeKey: key,
    })

    const window = getWindow()

    if (QA && window) {
        if (!window.__test_tracked) {
            window.__test_tracked = []
        }

        analyticsBrowser.addSourceMiddleware(({ payload, next }) => {
            const object = payload.obj

            const buffer: TrackBuffer = {
                type: object.type,
            }

            if (object.event) {
                buffer.eventName = object.event
            }

            if (object.properties) {
                buffer.properties = object.properties
            }

            if (object.traits) {
                buffer.traits = object.traits
            }

            if (object.userId) {
                buffer.userId = object.userId
            }

            if (object.options) {
                buffer.options = object.options
            }

            window.__test_tracked.push(buffer)

            return next(payload)
        })

        analyticsBrowser.on('reset', () => {
            window.__test_tracked.push({
                type: 'reset',
            })
        })
    }

    return analyticsBrowser
}

const segmentKey = 'segment'

const provideSegment = (app: App, segment: Segment): void => {
    app.provide(segmentKey, segment)
}

const normalizeMeFragmentIdentify = (me: MeFragment) => ({
    name: `${me.firstName} ${me.lastName}`,
    email: me.email,
    phone: me.customer?.billingAddress?.phoneNumber?.e164,
})

const normalizeMeFragmentLoginLogout = (me: MeFragment) => ({
    user_id: me.id,
    ...normalizeMeFragmentIdentify(me),
})

interface TrackPageArgs {
    url: string | undefined
    path: string | undefined
    title: string
}

interface Segment {
    track(...args: Parameters<AnalyticsBrowser['track']>): void

    login(me: MeFragment): void

    logout(me: MeFragment | null | undefined): void

    page(page: TrackPageArgs): void
}

export const useSegment = (): Segment => {
    return inject(segmentKey) as Segment
}
